import { Fragment, type ReactNode } from "react";

import type { IntlShape } from "react-intl";

import { Divider } from "@/components/atoms/Divider";
import {
  PriceLineItem,
  type PriceLineItemTooltip,
} from "@/components/molecules/PriceLineItem";

import type { PriceFacts, PriceRow, RowId } from "./utils/types";
import { toPriceRowProps } from "./utils/ui/toPriceRowProps";

type RenderRowsProps = {
  currency: string;
  excludeDecimals: boolean;
  facts?: PriceFacts | null;
  intl: IntlShape;
  /** Adapter wraps the DIY space rental tooltip's "View pricing rules" link. */
  renderPricingRulesLink?: (
    linkLabel: ReactNode,
    closeTooltip: () => void,
  ) => ReactNode;
  rows: PriceRow[];
  showExplainers: boolean;
  testId: string;
  /** Per-row tooltip override, keyed by `RowId`. See `toPriceRowProps`. */
  tooltipOverrides?: Partial<Record<RowId, PriceLineItemTooltip>>;
};

/**
 * Maps semantic `PriceRow`s to `PriceLineItem`s, inserting dividers when
 * `dividerBefore` is set.
 */
const RenderRows = ({
  currency,
  excludeDecimals,
  facts,
  intl,
  renderPricingRulesLink,
  rows,
  showExplainers,
  testId,
  tooltipOverrides,
}: RenderRowsProps) => (
  <>
    {rows.map((row) => {
      const lineItemProps = toPriceRowProps({
        currency,
        excludeDecimals,
        facts,
        intl,
        renderPricingRulesLink,
        row,
        showExplainers,
        tooltipOverrides,
      });

      return (
        <Fragment key={row.id}>
          {row.dividerBefore && <Divider spacing={0} />}
          <PriceLineItem
            caption={lineItemProps.caption}
            captionValue={lineItemProps.captionValue}
            color={lineItemProps.color}
            emphasis={lineItemProps.emphasis}
            label={lineItemProps.label}
            leftOffset={lineItemProps.leftOffset}
            middleContent={lineItemProps.middleContent}
            testId={lineItemProps.testId ?? `${testId}-${row.id}`}
            tooltip={lineItemProps.tooltip}
            value={lineItemProps.value}
          />
        </Fragment>
      );
    })}
  </>
);

export { RenderRows };
